import React from "react";
import { useLocale } from "@/contexts/LocaleContext";
import { useSettings } from "@/contexts/SettingsContext";
import { Coffee, Languages } from "lucide-react";

/**
 * Full-page maintenance screen.
 * Bilingual, branded dark+gold, with a small locale switcher pill.
 */
export default function MaintenanceScreen({ message }) {
  const { locale, setLocale } = useLocale();
  const { settings } = useSettings();
  const name = settings?.business_name || "King's Café";
  const text = message?.[locale] || message?.hr || "Trenutno održavamo stranicu. Vraćamo se uskoro.";
  const heading = locale === "en" ? "We'll be right back" : "Vraćamo se uskoro";

  return (
    <div
      className="min-h-screen bg-background text-foreground flex flex-col"
      data-testid="maintenance-screen"
      role="main"
      aria-live="polite"
    >
      {/* Top bar with locale switch */}
      <div className="flex justify-end px-5 sm:px-8 pt-5">
        <div
          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-border bg-card"
          role="group"
          aria-label="Language switcher"
        >
          <Languages className="w-3.5 h-3.5 text-primary" aria-hidden />
          <button
            type="button"
            onClick={() => setLocale("hr")}
            className={`px-2 py-0.5 rounded-full uppercase tracking-widest ${
              locale === "hr"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="maintenance-lang-hr"
            aria-pressed={locale === "hr"}
          >
            HR
          </button>
          <button
            type="button"
            onClick={() => setLocale("en")}
            className={`px-2 py-0.5 rounded-full uppercase tracking-widest ${
              locale === "en"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="maintenance-lang-en"
            aria-pressed={locale === "en"}
          >
            EN
          </button>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-xl w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border border-primary/40 bg-primary/5 mb-8">
            <Coffee className="w-7 h-7 text-primary" aria-hidden />
          </div>
          <p className="text-xs uppercase tracking-[0.4em] text-primary/80 mb-4">{name}</p>
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl"
            data-testid="maintenance-heading"
          >
            {heading}
            <span className="gold-text">.</span>
          </h1>
          <p
            className="text-base sm:text-lg text-muted-foreground mt-6 leading-relaxed"
            data-testid="maintenance-message"
          >
            {text}
          </p>
          <div className="mt-10 inline-flex items-center gap-2 text-xs text-muted-foreground">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" aria-hidden />
            {locale === "en" ? "Check back soon" : "Vratite se uskoro"}
          </div>
        </div>
      </div>

      <footer className="px-5 sm:px-8 py-6 text-center text-[11px] text-muted-foreground">
        © {new Date().getFullYear()} {name}
      </footer>
    </div>
  );
}
